import type { EventPersonalMessageBody } from "@anggunpraya-templates/base/app/schemas/event";
import type { BaseResponse, Event } from "@anggunpraya-templates/types";
import { EVENT_GUEST_ATTENDING_WITH_OBJECT } from "@anggunpraya-templates/base/app/constant";
import { useEventGuestStore } from "../stores/event-guest";

/**
 * Opsi tambahan untuk `useWishesForm()`.
 *
 * Hook `onSubmitted` biasanya dipakai oleh feed wishes untuk memicu refresh
 * setelah request create berhasil.
 */
interface UseWishesFormOptions {
  onSubmitted?: () => void;
}

/**
 * Composable shared untuk flow submit form wishes.
 *
 * Tanggung jawab composable ini:
 * - autofill nama tamu dari guest aktif
 * - menentukan mode simulasi vs request nyata
 * - mengirim data ke API
 * - mengembalikan state form yang siap di-bind oleh komponen UI
 *
 * @param options Opsi tambahan seperti callback setelah submit sukses
 * @returns State form, state loading, dan action submit untuk komponen wishes
 *
 * @example
 * ```typescript
 * const { state, loading, submit } = useWishesForm({
 *   onSubmitted: refreshFeed,
 * });
 * ```
 */
export function useWishesForm(options: UseWishesFormOptions = {}) {
  const { $services } = useNuxtApp();
  const { eventGuest } = storeToRefs(useEventGuestStore());
  const toastNotification = useToastNotification();
  const { flavor } = useRuntimeConfig().public;
  const { data: eventDetail } = useNuxtData<BaseResponse<Event>>("event-detail");
  const { isEventPreview, isGuestBased } = useInvitationContext();
  const { guestText } = useOpeningCard();

  const { loading, setLoading, resetLoading } = useLoadingControl<"submit">();

  const state = ref<EventPersonalMessageBody>({
    media_id: null,
    event_guest_id: null,
    guest_name: "",
    status: "",
    message: "",
  });

  const showAttendanceField = computed(() => !isGuestBased.value);

  /** Sinkronkan mode guest/public ke state form agar UI tidak perlu mapping sendiri. */
  watch(
    [() => eventGuest.value, () => isGuestBased.value] as const,
    ([guest, guestBased]) => {
      if (!guestBased) {
        state.value = {
          ...state.value,
          event_guest_id: null,
          guest_name: guestText.value || "",
        };

        return;
      }

      if (!guest) {
        state.value = {
          ...state.value,
          event_guest_id: null,
          guest_name: "",
        };

        return;
      }

      const { name, attending_with, event_guest_id } = guest;
      const guestName = attending_with !== "alone"
        ? `${name} & ${EVENT_GUEST_ATTENDING_WITH_OBJECT[attending_with] || ""}`.trim()
        : name;

      state.value = {
        ...state.value,
        event_guest_id,
        guest_name: guestName,
        status: "",
      };
    },
    {
      immediate: true,
    },
  );

  function resetStateAfterSubmit() {
    if (isGuestBased.value) {
      state.value = {
        ...state.value,
        message: "",
        status: "",
      };

      return;
    }

    state.value = {
      ...state.value,
      event_guest_id: null,
      status: "",
      message: "",
    };
  }

  /**
   * Submit wishes ke API atau jalankan simulasi untuk preview/portfolio.
   * UI cukup memanggil function ini tanpa mengetahui policy flavor.
   *
   * @returns Promise yang selesai setelah submit atau simulasi selesai dijalankan
   */
  async function submit() {
    try {
      setLoading("submit");

      const eventId = eventDetail.value?.data?.event_id;

      if (["preview", "portfolio"].includes(flavor) || isEventPreview.value) {
        await new Promise(resolve => setTimeout(resolve, 2000));

        toastNotification.add("success", {
          title: "Berhasil (Simulasi)",
          description: "Ucapan & doa Anda telah berhasil dikirim. Terima kasih atas partisipasi Anda!",
        });

        resetStateAfterSubmit();
        return;
      }

      if (!eventId) {
        toastNotification.add("error", {
          title: "Gagal",
          description: "Data event tidak ditemukan.",
        });

        return;
      }

      if (isGuestBased.value && !eventGuest.value) {
        toastNotification.add("error", {
          title: "Gagal",
          description: "Data tamu tidak ditemukan.",
        });

        return;
      }

      const payload: EventPersonalMessageBody = {
        ...state.value,
        event_guest_id: isGuestBased.value
          ? (eventGuest.value?.event_guest_id || state.value.event_guest_id)
          : null,
        status: isGuestBased.value ? "" : state.value.status,
      };

      await $services.event.personalMessage.create(
        eventId,
        payload,
      );

      toastNotification.add("success", {
        title: "Berhasil",
        description: "Ucapan & doa Anda telah berhasil dikirim. Terima kasih atas partisipasi Anda!",
      });

      options.onSubmitted?.();
      resetStateAfterSubmit();
    }
    catch (error) {
      if (error instanceof Error) {
        const message = error.message || "Terjadi kesalahan saat mengirim ucapan & doa.";

        console.error(message);
        toastNotification.add("error", {
          title: "Gagal",
          description: message,
        });
      }
    }
    finally {
      resetLoading();
    }
  }

  return {
    state,
    loading,
    showAttendanceField,
    submit,
  };
}
